import { Alert, Checkbox, Space, Spin, Tag, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { projectsApi } from '@api/projects';
import { refreshSessionFromCookie } from '@api/http';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';

const { Text } = Typography;

interface Props {
  userId: string;
  /** SuperAdmin tüm projelere otomatik erişir; atama gerekmez. */
  isSuperAdmin: boolean;
}

/**
 * REQ-45 / K-A — kullanıcı-proje atama bölümü (admin kullanıcı düzenleme
 * çekmecesinde). `project.manage` gerektirir; erişim yoksa hiç render edilmez.
 * SuperAdmin kullanıcılar için yalnız bilgilendirme notu gösterilir.
 */
export function UserProjectsSection({ userId, isSuperAdmin }: Props) {
  const { t } = useTranslation(['projects', 'common']);
  const qc = useQueryClient();
  const canManageProjects = useCan(PERMISSIONS.PROJECT_MANAGE);
  const enabled = canManageProjects && !isSuperAdmin;

  const { data: allProjects = [], isLoading: loadingAll } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
    enabled,
  });
  const { data: assigned = [], isLoading: loadingAssigned } = useQuery({
    queryKey: ['user-projects', userId],
    queryFn: () => projectsApi.listUserProjects(userId),
    enabled,
  });

  const refetch = () => qc.invalidateQueries({ queryKey: ['user-projects', userId] });

  // REQ-45 — atama/çıkarma, kullanıcının erişilebilir projelerini değiştirir.
  // Admin kendine proje atarsa kendi switcher'ı logout'suz güncellensin diye
  // oturumu tazeler (setSession `availableProjects`'i yeniler).
  const assignMutation = useMutation({
    mutationFn: (projectId: string) => projectsApi.assignUserProject(userId, projectId),
    onSuccess: async (_r, projectId) => {
      toast.success(t('assign.assigned'));
      await refetch();
      await refreshSessionFromCookie();
      void recordAuditAction({
        action: 'adminUser.project.assign',
        resource: 'adminUser',
        resourceId: userId,
        summary: `${userId} kullanıcısı ${projectId} projesine eklendi`,
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const removeMutation = useMutation({
    mutationFn: (projectId: string) => projectsApi.removeUserProject(userId, projectId),
    onSuccess: async (_r, projectId) => {
      toast.success(t('assign.removed'));
      await refetch();
      await refreshSessionFromCookie();
      void recordAuditAction({
        action: 'adminUser.project.remove',
        resource: 'adminUser',
        resourceId: userId,
        summary: `${userId} kullanıcısı ${projectId} projesinden çıkarıldı`,
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  if (!canManageProjects) return null;

  return (
    <div data-testid="user-projects-section">
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {t('assign.heading')}
      </Text>

      {isSuperAdmin ? (
        <Alert type="info" showIcon message={t('assign.superAdminNote')} />
      ) : loadingAll || loadingAssigned ? (
        <Spin size="small" />
      ) : allProjects.length === 0 ? (
        <Text type="secondary">{t('assign.empty')}</Text>
      ) : (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
            {t('assign.description')}
          </Text>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {allProjects.map((p) => {
              const isAssigned = assigned.some((a) => a.id === p.id);
              const pending =
                (assignMutation.isPending && assignMutation.variables === p.id) ||
                (removeMutation.isPending && removeMutation.variables === p.id);
              return (
                <Checkbox
                  key={p.id}
                  checked={isAssigned}
                  disabled={pending}
                  onChange={(e) => {
                    if (e.target.checked) assignMutation.mutate(p.id);
                    else removeMutation.mutate(p.id);
                  }}
                >
                  <Space>
                    <Text>{p.name}</Text>
                    {p.isDefault ? <Tag color="gold">{t('badges.default')}</Tag> : null}
                    <Text type="secondary" style={{ fontSize: 11 }} code>
                      {p.slug}
                    </Text>
                  </Space>
                </Checkbox>
              );
            })}
          </Space>
        </>
      )}
    </div>
  );
}
